import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { beaconClient } from '@/lib/api/client.js';

interface Integration {
  id: string;
  kind: 'harvest' | 'slack' | 'resend';
  label: string;
  configJson: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const integrationKeys = {
  all: ['integrations'] as const,
  list: () => [...integrationKeys.all, 'list'] as const,
};

export function useIntegrations() {
  return useQuery({
    queryKey: integrationKeys.list(),
    queryFn: async (): Promise<Integration[]> => {
      const data = await beaconClient.get<{ integrations: Integration[] }>(
        '/api/integrations'
      );
      return data.integrations;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: 'harvest' | 'slack' | 'resend';
      label: string;
      configJson?: Record<string, unknown>;
    }) => beaconClient.post<{ id: string }>('/api/integrations', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

export function useUpdateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      label?: string;
      configJson?: Record<string, unknown>;
      isActive?: boolean;
    }) => beaconClient.patch(`/api/integrations/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => beaconClient.delete(`/api/integrations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}
